import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BAN_CATEGORIES } from "@/lib/constants";
import { formatIdentifiers } from "@/lib/format";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api } from "@/lib/api";

// Moderator + Owner only (the parent hides this for Support; the server enforces it too).
export default function UserActions({ user, onUser, onBanned }) {
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("other");
  const [banResult, setBanResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const banned = user.status === "banned";

  async function toggleHide() {
    setBusy(true);
    try {
      const res = await api(`/admin/users/${user.id}/${user.hidden ? "unhide" : "hide"}`, { method: "POST" });
      onUser(res.user);
      toast.success(user.hidden ? "Profile is visible again." : "Profile hidden.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  const ban = async () => {
    const res = await api(`/admin/users/${user.id}/ban`, { body: { reason: reason.trim() || null, category } });
    toast.success("User banned.");
    setBanResult({ case: res.ban_case_id, ids: res.banned_identifiers });
    setReason("");
    onUser(res.user);
  };

  const unban = async () => {
    await api(`/admin/users/${user.id}/unban`, { method: "POST" });
    toast.success("Ban lifted.");
    onBanned();
  };

  return (
    <div className="flex flex-col items-end gap-3">
    {banResult && (
      <div className="cw-tone-error max-w-md rounded-2xl px-4 py-3 text-xs" role="status" data-testid="ban-result">
        <p className="font-semibold">Ban case {banResult.case}</p>
        <p className="mt-1 break-all" data-testid="ban-result-identifiers">Blocked: {formatIdentifiers(banResult.ids)}</p>
      </div>
    )}
    <div className="flex flex-wrap gap-2" data-testid="user-actions">
      {!banned && (
        <Button variant="outline" onClick={toggleHide} disabled={busy || user.status === "deleted"} data-testid={user.hidden ? "user-unhide-button" : "user-hide-button"}>
          {user.hidden ? "Unhide" : "Hide"}
        </Button>
      )}
      {banned
        ? <Button variant="outline" onClick={() => setDialog("unban")} data-testid="user-unban-button">Unban</Button>
        : <Button variant="destructive" onClick={() => setDialog("ban")} disabled={user.status === "deleted"} data-testid="user-ban-button">Ban</Button>}
      <ConfirmDialog open={dialog === "ban"} onOpenChange={(o) => !o && setDialog(null)} testId="ban-confirm-dialog"
        title={`Ban ${user.phone}?`} confirmLabel="Ban user" onConfirm={ban}
        description="This blocks their phone number and every device on the account, hides the profile and signs them out.">
        <div>
          <Label htmlFor="ban-reason">Reason (optional)</Label>
          <Input id="ban-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What happened" className="mt-2" data-testid="ban-reason-input" />
        </div>
        <div>
          <Label htmlFor="ban-category">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger id="ban-category" className="mt-2" data-testid="ban-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(BAN_CATEGORIES).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`ban-category-option-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <p className="cw-muted mt-1 text-xs">Shown to them on the banned screen and used for the appeal.</p>
        </div>
      </ConfirmDialog>
      <ConfirmDialog open={dialog === "unban"} onOpenChange={(o) => !o && setDialog(null)} testId="unban-confirm-dialog" destructive={false}
        title={`Lift the ban on ${user.phone}?`} confirmLabel="Unban" onConfirm={unban}
        description="Their phone and devices can sign in again and the profile becomes visible." />
    </div>
    </div>
  );
}
